/**
 * Enrollment Policy Utility
 * 
 * Fetches enrollment policies from the centralized enrollment_policy table.
 * All policies are managed via FK columns pointing to reusable templates.
 * 
 * For Courses:
 * 1. Check if course_run has enrollment_policy_id → use that template
 * 2. Else check if course_type has enrollment_policy_id → use that template
 * 3. Fall back to system defaults
 * 
 * For Certifications:
 * 1. Check if full_certification_runs has enrollment_policy_id → use that template
 * 2. Else check if full_certification has enrollment_policy_id → use that template
 * 3. Fall back to system defaults
 */

/**
 * Default policy values used when no policy is configured for a course type.
 * These match the current hardcoded behavior in Registration.js and FullCertRegistration.js.
 */
export const DEFAULT_ENROLLMENT_POLICY = {
    needs_email_verification: true,
    needs_approval: false,
    is_ssg_course: false,
    enable_skills_future_credit_pay: false,
    requires_documents: true,
    gate_on_course_run_confirmed: false,
    commitment_type: 'full',
    registration_fee_amount: null,
    first_installment_amount: null,
    full_fee_amount: null,
    number_of_installments: 0,
};

/**
 * Fetches an enrollment policy template by ID from the enrollment_policy table.
 * 
 * @param {Object} supabase - Supabase client instance
 * @param {string} policyId - The UUID of the enrollment policy
 * @returns {Promise<Object|null>} The policy template or null
 */
async function fetchPolicyTemplate(supabase, policyId) {
    if (!policyId) return null;
    
    const { data, error } = await supabase
        .from('enrollment_policy')
        .select('*')
        .eq('id', policyId)
        .eq('is_active', true)
        .single();
    
    if (error && error.code !== 'PGRST116') {
        console.error('Error fetching enrollment_policy template:', error);
    }
    
    return data || null;
}

/**
 * Fetches the effective enrollment policy for a course run.
 * Uses hierarchical resolution:
 * 1. course_run.enrollment_policy_id (if set)
 * 2. course_type.enrollment_policy_id (if set)
 * 3. System defaults
 * 
 * @param {Object} supabase - Supabase client instance
 * @param {string} courseTypeCode - The course type code (e.g., 'D1', 'TEST_FREE')
 * @param {string} courseRunId - The course run ID (e.g., 'D1-1', 'TEST_FREE-1')
 * @returns {Promise<Object>} The effective enrollment policy
 */
export async function getEffectivePolicy(supabase, courseTypeCode, courseRunId) {
    if (!courseTypeCode) {
        console.warn('getEffectivePolicy: No courseTypeCode provided, returning defaults');
        return { ...DEFAULT_ENROLLMENT_POLICY };
    }

    try {
        let basePolicy = null;
        let policySource = 'defaults';

        // Step 1: Check if course_run has an enrollment_policy_id
        if (courseRunId) {
            const { data: courseRun, error: runError } = await supabase
                .from('course_run')
                .select('enrollment_policy_id')
                .eq('id', courseRunId)
                .single();
            
            if (runError && runError.code !== 'PGRST116') {
                console.error('Error fetching course_run:', runError);
            }
            
            if (courseRun?.enrollment_policy_id) {
                basePolicy = await fetchPolicyTemplate(supabase, courseRun.enrollment_policy_id);
                if (basePolicy) {
                    policySource = 'course_run_template';
                }
            }
        }

        // Step 2: If no run policy, check course_type.enrollment_policy_id
        if (!basePolicy) {
            const { data: courseType, error: typeError } = await supabase
                .from('course_type')
                .select('enrollment_policy_id')
                .eq('code', courseTypeCode)
                .single();
            
            if (typeError && typeError.code !== 'PGRST116') {
                console.error('Error fetching course_type:', typeError);
            }
            
            if (courseType?.enrollment_policy_id) {
                basePolicy = await fetchPolicyTemplate(supabase, courseType.enrollment_policy_id);
                if (basePolicy) {
                    policySource = 'course_type_template';
                }
            }
        }

        // If no policy found, use defaults
        if (!basePolicy) {
            console.log(`No enrollment policy found for course type: ${courseTypeCode}, using defaults`);
            return { ...DEFAULT_ENROLLMENT_POLICY, _source: 'defaults' };
        }

        // Merge: defaults + base policy
        const effectivePolicy = {
            ...DEFAULT_ENROLLMENT_POLICY,
            ...basePolicy,
            _source: policySource, // Include source for debugging
        };

        // Remove internal fields that shouldn't be in the effective policy
        delete effectivePolicy.id;
        delete effectivePolicy.created_at;
        delete effectivePolicy.updated_at;
        delete effectivePolicy.is_system_default;
        delete effectivePolicy.is_active;

        return effectivePolicy;
    } catch (error) {
        console.error('Error in getEffectivePolicy:', error);
        return { ...DEFAULT_ENROLLMENT_POLICY };
    }
}

/**
 * Fetches the effective enrollment policy for a full certification run.
 * Uses hierarchical resolution:
 * 1. full_certification_runs.enrollment_policy_id (if set)
 * 2. full_certification.enrollment_policy_id (if set)
 * 3. System defaults
 * 
 * @param {Object} supabase - Supabase client instance
 * @param {string} certCode - The certification code (e.g., 'CERT1')
 * @param {string} certRunId - The certification run ID (e.g., 'CERT1-RUN1')
 * @returns {Promise<Object>} The effective enrollment policy
 */
export async function getEffectiveCertPolicy(supabase, certCode, certRunId) {
    if (!certCode) {
        console.warn('getEffectiveCertPolicy: No certCode provided, returning defaults');
        return { ...DEFAULT_ENROLLMENT_POLICY };
    }

    try {
        let basePolicy = null;
        let policySource = 'defaults';

        // Step 1: Check if cert_run has an enrollment_policy_id
        if (certRunId) {
            const { data: certRun, error: runError } = await supabase
                .from('full_certification_runs')
                .select('enrollment_policy_id')
                .eq('id', certRunId)
                .single();
            
            if (runError && runError.code !== 'PGRST116') {
                console.error('Error fetching full_certification_runs:', runError);
            }
            
            if (certRun?.enrollment_policy_id) {
                basePolicy = await fetchPolicyTemplate(supabase, certRun.enrollment_policy_id);
                if (basePolicy) {
                    policySource = 'cert_run_template';
                }
            }
        }

        // Step 2: If no run policy, check full_certification.enrollment_policy_id
        if (!basePolicy) {
            const { data: cert, error: certError } = await supabase
                .from('full_certification')
                .select('enrollment_policy_id')
                .eq('code', certCode)
                .single();
            
            if (certError && certError.code !== 'PGRST116') {
                console.error('Error fetching full_certification:', certError);
            }
            
            if (cert?.enrollment_policy_id) {
                basePolicy = await fetchPolicyTemplate(supabase, cert.enrollment_policy_id);
                if (basePolicy) {
                    policySource = 'cert_template';
                }
            }
        }

        // If no policy found, use defaults
        if (!basePolicy) {
            console.log(`No enrollment policy found for certification: ${certCode}, using defaults`);
            return { ...DEFAULT_ENROLLMENT_POLICY, _source: 'defaults' };
        }

        // Merge: defaults + base policy
        const effectivePolicy = {
            ...DEFAULT_ENROLLMENT_POLICY,
            ...basePolicy,
            _source: policySource,
        };

        // Remove internal fields
        delete effectivePolicy.id;
        delete effectivePolicy.created_at;
        delete effectivePolicy.updated_at;
        delete effectivePolicy.is_system_default;
        delete effectivePolicy.is_active;

        return effectivePolicy;
    } catch (error) {
        console.error('Error in getEffectiveCertPolicy:', error);
        return { ...DEFAULT_ENROLLMENT_POLICY };
    }
}

/**
 * Checks if email verification is required based on the effective policy.
 * This is a convenience function for the most common check.
 * 
 * @param {Object} policy - The effective enrollment policy
 * @returns {boolean} Whether email verification is required
 */
export function requiresEmailVerification(policy) {
    if (!policy) return true; // Default to requiring verification
    return policy.needs_email_verification !== false;
}

/**
 * Checks if admin approval is required based on the effective policy.
 * 
 * @param {Object} policy - The effective enrollment policy
 * @returns {boolean} Whether admin approval is required
 */
export function requiresApproval(policy) {
    if (!policy) return false; // Default to not requiring approval
    return policy.needs_approval === true;
}

/**
 * Checks if the course run needs to be confirmed before proceeding.
 * 
 * @param {Object} policy - The effective enrollment policy
 * @param {boolean} isRunConfirmed - Whether the course run is confirmed
 * @returns {boolean} Whether to gate on course run confirmation
 */
export function shouldGateOnRunConfirmation(policy, isRunConfirmed) {
    if (!policy) return false;
    if (policy.gate_on_course_run_confirmed && !isRunConfirmed) {
        return true;
    }
    return false;
}

/**
 * Gets the commitment type from the policy (what payment is required upfront).
 * 
 * @param {Object} policy - The effective enrollment policy
 * @returns {string|null} 'registration_fee' | 'first_installment' | 'full' | null
 */
export function getCommitmentType(policy) {
    if (!policy) return 'full';
    return policy.commitment_type || 'full';
}

/**
 * Determines the correct registration stage after form submission based on policy.
 *
 * Fixed step sequence (steps that are disabled are skipped):
 *   1. Registration Form          — always (this function returns the NEXT stage after it)
 *   2. Email Verification         — handled before this function is called
 *   3. Course Run Gate            — if gate_on_course_run_confirmed = true → 'interest_received'
 *   4. Registration Fee Payment   — if commitment_type = 'registration_fee' → 'registration_fee_pending'
 *   5. Admin Approval             — if needs_approval = true → 'pending_approval'
 *   6. Enrollment Documents       — if requires_documents = true → 'documents_pending'
 *   7. Final Payment              — if commitment_type not in ('interest_registration','none') → 'payment_pending'
 *   8. Enrolled                   — 'enrolled' (interest_registration / none)
 *
 * @param {Object} policy - The effective enrollment policy
 * @param {boolean} isRunConfirmed - Whether the course run is confirmed
 * @returns {string} The registration stage to set
 */
export function getNextRegistrationStage(policy, isRunConfirmed = true) {
    if (!policy) return 'pending';

    const commitmentType = policy.commitment_type || 'full';

    // Step 3: Course Run Gate
    if (policy.gate_on_course_run_confirmed && !isRunConfirmed) {
        return 'interest_received';
    }

    // Step 4: Registration Fee Payment (only for registration_fee commitment)
    if (commitmentType === 'registration_fee') {
        return 'registration_fee_pending';
    }

    // Step 5: Admin Approval
    if (policy.needs_approval) {
        return 'pending_approval';
    }

    // Step 6: Enrollment Documents
    if (policy.requires_documents) {
        return 'documents_pending';
    }

    // Step 7: Final Payment (paid commitment types reach here)
    if (commitmentType !== 'interest_registration' && commitmentType !== 'none') {
        return 'payment_pending';
    }

    // Step 8: Enrolled (interest_registration / none — no payment required)
    return 'enrolled';
}

/**
 * Determines the URL to navigate to after form submission.
 * All registration views are now unified under /register/:id
 * The RegistrationHub component determines which view to render based on stage.
 * 
 * @param {Object} policy - The effective enrollment policy (unused but kept for API consistency)
 * @param {string} registrationId - The registration ID
 * @param {boolean} isRunConfirmed - Whether the course run is confirmed (unused but kept for API consistency)
 * @returns {string} The URL to navigate to
 */
export function getNextNavigationUrl(policy, registrationId, isRunConfirmed = true) {
    // All views are now rendered under /register/:id
    // RegistrationHub determines which component to show based on registration_stage
    return `/register/${registrationId}`;
}
